"use client";

import { useRef, useState, useCallback } from "react";
import { useScroll, useTransform, motion, useReducedMotion } from "framer-motion";
import { useLanguage } from "@/context/language-context";
import { ArrowRight } from "lucide-react";
import { ContactModal } from "@/components/contact-modal";

export default function Hero() {
  const { content } = useLanguage();
  const containerRef = useRef<HTMLDivElement>(null);
  const [contactOpen, setContactOpen] = useState(false);
  const reducedMotion = useReducedMotion();

  const { scrollY } = useScroll();
  const opacity = useTransform(scrollY, [0, 800], [1, 0]);
  const scale = useTransform(scrollY, [0, 800], [1, 0.94]);
  const y = useTransform(scrollY, [0, 800], [0, -150]);

  const track1 = [
    "/hero-slider/mikkel-3.webp",
    "/hero-slider/mikkel-1.webp",
    "/hero-slider/mikkel-4.webp",
    "/hero-slider/mikkel-2.webp",
    "/hero-slider/mikkel-5.webp",
  ];

  const track2 = [
    "/hero-slider/mikkel-2.webp",
    "/hero-slider/mikkel-5.webp",
    "/hero-slider/mikkel-1.webp",
    "/hero-slider/mikkel-3.webp",
    "/hero-slider/mikkel-4.webp",
  ];

  const col1Images = [...track1, ...track1];
  const col2Images = [...track2, ...track2];

  const scrollToProjects = useCallback(() => {
    const projectsSection = document.getElementById("projects");
    if (projectsSection) {
      projectsSection.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  return (
    <section
      ref={containerRef}
      className="sticky top-0 flex h-screen w-full flex-col justify-between overflow-hidden bg-background px-container pt-28 pb-12 sm:pt-32 sm:pb-16 md:px-16 2xl:pb-24"
      id="home"
    >
      <motion.div
        style={{ opacity }}
        className="pointer-events-none absolute top-0 right-6 bottom-0 [isolation:isolate] z-5 flex h-full w-55 gap-3 overflow-hidden px-2 opacity-[0.18] select-none sm:right-12 sm:w-65 sm:gap-4 md:right-16 md:w-85 lg:right-24 lg:w-100 xl:right-36 xl:w-110 2xl:right-48 2xl:w-120 dark:opacity-[0.24]"
      >
        <div className="relative h-full flex-1 overflow-hidden max-sm:hidden">
          <motion.div
            animate={reducedMotion ? undefined : { y: ["0%", "-50%"] }}
            transition={{
              ease: "linear",
              duration: 32,
              repeat: Infinity,
            }}
            className="flex flex-col gap-3 pt-4 sm:gap-4"
          >
            {col1Images.map((src, idx) => (
              <div
                key={idx}
                className="relative aspect-3/4 w-full overflow-hidden rounded-4xl border border-border/5"
              >
                <img
                  src={src}
                  alt="Mikkel Scheike"
                  aria-hidden="true"
                  onError={(e) => {
                    e.currentTarget.src = "/images/mikkel.webp";
                  }}
                  className="h-full w-full object-cover object-center brightness-[0.92] contrast-[1.05] grayscale dark:brightness-[0.8]"
                />
              </div>
            ))}
          </motion.div>
        </div>

        <div className="relative h-full flex-1 overflow-hidden max-sm:opacity-50">
          <motion.div
            animate={reducedMotion ? undefined : { y: ["-50%", "0%"] }}
            transition={{
              ease: "linear",
              duration: 32,
              repeat: Infinity,
            }}
            className="flex flex-col gap-3 pt-4 sm:gap-4"
          >
            {col2Images.map((src, idx) => (
              <div
                key={idx}
                className="relative aspect-3/4 w-full overflow-hidden rounded-4xl border border-border/5"
              >
                <img
                  src={src}
                  alt="Mikkel Scheike"
                  aria-hidden="true"
                  onError={(e) => {
                    e.currentTarget.src = "/images/mikkel.webp";
                  }}
                  className="h-full w-full object-cover object-center brightness-[0.92] contrast-[1.05] grayscale dark:brightness-[0.8]"
                />
              </div>
            ))}
          </motion.div>
        </div>

        <div className="pointer-events-none absolute inset-0 z-10 bg-linear-to-t from-background via-transparent to-background" />
        <div className="pointer-events-none absolute inset-0 z-10 bg-linear-to-r from-background via-transparent to-transparent" />
      </motion.div>

      <motion.div
        style={reducedMotion ? { opacity } : { opacity, scale, y }}
        className="relative z-20 flex h-full w-full flex-1 flex-col justify-end gap-6 will-change-[opacity,transform] sm:gap-12 xl:gap-16"
      >
        <div className="flex w-full items-start justify-between">
          <div className="grunge-text pointer-events-none rotate-90 text-4xl text-muted-foreground/30 select-none sm:text-6xl">
            {"////"}
          </div>

          <div className="flex flex-col items-center gap-4">
            <div className="relative h-12 w-px overflow-hidden bg-border">
              <motion.div
                className="absolute top-0 left-0 h-1/2 w-full bg-foreground"
                animate={reducedMotion ? undefined : { y: ["0%", "100%", "0%"] }}
                transition={{
                  duration: 2.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
            </div>
            <span className="font-mono text-[9px] tracking-[0.3em] text-muted-foreground uppercase [writing-mode:vertical-lr]">
              SCROLL
            </span>
          </div>
        </div>

        <div className="relative z-20 mt-auto flex w-full flex-col justify-center">
          <div className="overflow-hidden">
            <h1 className="text-5xl leading-[0.85] font-black tracking-tighter text-balance text-foreground uppercase sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl 2xl:text-[clamp(5rem,7vw,6rem)]">
              {content.hero.name}
              <br />
              <span className="text-foreground/80">{content.hero.subtitle}</span>
            </h1>
          </div>
        </div>

        <div className="space-y-6 sm:space-y-10">
          <p className="max-w-xl leading-relaxed font-light text-pretty text-muted-foreground sm:text-lg 2xl:text-xl">
            {content.about.description}
          </p>

          <div className="flex flex-col flex-wrap gap-4 sm:flex-row sm:items-center">
            <button
              type="button"
              onClick={() => setContactOpen(true)}
              className="focus-ring group relative flex h-12 w-fit cursor-pointer items-center justify-center overflow-hidden rounded-full border border-border/50 bg-foreground px-6 text-background shadow-2xl transition-all duration-500 ease-out hover:-translate-y-0.5 hover:border-foreground/30 hover:bg-background hover:text-foreground xl:h-16 xl:px-10"
            >
              <div className="absolute inset-0 flex h-full w-full -translate-x-full -skew-x-12 justify-center group-hover:translate-x-full group-hover:duration-1000">
                <div className="relative h-full w-8 bg-background/20 dark:bg-foreground/10" />
              </div>
              <span className="relative z-10 flex items-center gap-2 text-xs font-semibold tracking-[0.15em] uppercase xl:gap-3 xl:text-base">
                {content.hero.cta_primary}
                <ArrowRight className="h-3.5 w-3.5 transition-transform duration-500 group-hover:translate-x-1 xl:h-5 xl:w-5" />
              </span>
            </button>

            <button
              type="button"
              onClick={scrollToProjects}
              className="focus-ring group relative flex h-12 w-fit cursor-pointer items-center justify-center rounded-full border border-border px-6 text-muted-foreground transition-all duration-500 hover:border-border/30 hover:bg-secondary/15 hover:text-foreground sm:border-transparent xl:h-16 xl:px-10"
            >
              <span className="relative z-10 flex items-center gap-2 text-xs font-semibold tracking-[0.15em] uppercase xl:gap-3 xl:text-base">
                {content.hero.cta_secondary}
                <ArrowRight className="h-3.5 w-3.5 opacity-50 transition-all duration-500 group-hover:translate-x-1 group-hover:opacity-100 xl:h-5 xl:w-5" />
              </span>
            </button>
          </div>
        </div>
      </motion.div>

      <ContactModal open={contactOpen} onOpenChange={setContactOpen} />
    </section>
  );
}
